<template>
  <div
    class="product-recommendations critical-component-clear"
    :class="{
      [$class('error')]: state === 'error',
      [$class('loading')]: state === 'loading',
      'section-styles--bottom-padding': section.bottomPadding,
      'section-styles--divider': section.sectionDividers,
      'section-styles--top-padding': section.topPadding,
    }"
  >
    <div
      v-if="section.title || blocks.length > 1 || (activeCta?.link && activeCta?.text)"
      class="product-recommendations__header container"
      :class="headerClass"
    >
      <h2
        v-if="sectionTitle"
        :id="`section-title-${section.id}`"
        class="
          product-recommendations__title
          text-heading-2xs
          text-heading-s-desktop
        "
        v-text="sectionTitle"
      />

      <div
        v-if="blocks.length > 1"
        class="product-recommendations__tabs"
      >
        <btn
          v-for="(block, index) of blocks"
          :key="`tab-${block.title}-${index}`"
          class="product-recommendations__tab"
          :label="block.title"
          :modifiers="
            activeBlock.handle === block.handle
              ? 'tab active'
              : 'tab'
          "
          :show-icon="false"
          type="button"
          @click.prevent="handleTabClick(block)"
        />
      </div>

      <div
        v-if="activeCta?.link && activeCta?.text"
        class="
          product-recommendations__cta
          product-recommendations__cta--header
          text-right
        "
      >
        <btn
          class="
            product-recommendations__cta-link
            text-button-xs
            text-button-xs-desktop
          "
          :label="activeCta.text"
          modifiers="text"
          :show-icon="false"
          :url="activeCta.link"
        />
      </div>
    </div>

    <div
      class="product-recommendations__products-container container-no-padding"
      :style="{ minHeight }"
    >
      <splide
        ref="splide"
        :key="activeBlock.handle"
        class="product-recommendations__products"
        :has-track="false"
        :options="config"
        :aria-label="
          section.title
            ? `${section.title} - ${activeBlock.title}`
            : $string('accessibility.recommendations')
        "
        @splide:move="handleMove"
        @splide:hidden="(_, slide) => handleVisibleUpdate(slide, false)"
        @splide:visible="(_, slide) => handleVisibleUpdate(slide, true)"
      >
        <p
          v-if="state === 'error'"
          class="product-recommendations__error text-heading-2xs"
          v-text="$string('accessibility.error_loading_products')"
        />

        <splide-track>
          <splide-slide
            v-for="(product, index) of products"
            :key="`recommendation-${product?.handle}-${index}`"
          >
            <product-card
              block-class="product-recommendations"
              delay-click
              :index="index"
              :handle="product?.handle"
              :nosto-result-id="activePlacementResultId"
              :product="checkCurrencyMatch(product) ? product : false"
              :visible="visible[index]"
              :swiper-carousel="false"
              @view-product="(url) => handleViewProduct(product, url)"
            />
          </splide-slide>
        </splide-track>

        <div class="product-recommendations__arrows">
          <btn
            class="
              product-recommendations__arrow
              product-recommendations__arrow--prev
            "
            :label="$string('accessibility.carousel.previous')"
            modifiers="round"
            type="button"
            @click.prevent="handleArrowClick('previous')"
          >
            <template #icon>
              <icon-chevron-left />
            </template>
          </btn>

          <btn
            class="
              product-recommendations__arrow
              product-recommendations__arrow--next
            "
            :label="$string('accessibility.carousel.next')"
            modifiers="round"
            type="button"
            @click.prevent="handleArrowClick('next')"
          >
            <template #icon>
              <icon-chevron-right />
            </template>
          </btn>
        </div>

        <ul class="product-recommendations__pagination splide__pagination" />
      </splide>
    </div>

    <div
      v-if="activeCta?.link && activeCta?.text"
      class="product-recommendations__footer container"
    >
      <btn
        class="product-recommendations__cta"
        :label="activeCta.text"
        modifiers="text"
        :show-icon="false"
        :url="activeCta.link"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product recommendations (product-recommendations)
 * -----------------------------------------------------------------------------
 * Nosto powered product recommendation carousel.
 *
 * @param {Array{}} blocks - Section blocks.
 * @param {Object} section - Section settings.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductCard from '~async/product-card/product-card'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import { values as breakpoints } from '~/config/breakpoints'

import { waitFor } from '~/helpers/general'

export default {
  name: 'ProductRecommendations',

  components: {
    Btn,
    IconChevronLeft,
    IconChevronRight,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [
        {
          cta: {},
          handle: 'loading',
          placements: [],
          title: '',
        },
      ],
    },
    section: {
      type: Object,
      default: () => ({
        bottomPadding: true,
        cta: {},
        fallbackTitle: '',
        id: '',
        sectionDividers: true,
        title: '',
        topPadding: true,
      }),
    },
  },

  data() {
    return {
      activeBlock: {
        handle: 'loading',
      },
      config: {
        breakpoints: {
          [breakpoints.l]: {
            arrows: false,
            drag: false,
            destroy: false,
            gap: 'var(--gutter-desktop)',
            keyboard: true,
            pagination: true,
            perMove: 4,
            perPage: 4,
            speed: this.$timing('slower'),
            type: 'slide',
          },
        },
        destroy: true,
        direction: cnvs.store.language === 'ar' ? 'rtl' : 'ltr',
        mediaQuery: 'min',
      },
      minHeight: 0,
      slides: {
        current: 0,
        last: 0,
      },
      products: [false, false, false, false],
      sectionTitle: false,
      state: 'loading',
      storageKey: 'nosto-click',
      visible: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      nostoRecommendations: (state) => state.nosto.recommendations,
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Compute CTA object based on active block.
     * @returns {Object}
     */
    activeCta() {
      return this.activeBlock?.cta?.link && this.activeBlock?.cta?.text
        ? this.activeBlock.cta
        : this.section.cta
    },

    /**
     * Compute active block placement.
     * @return {String}
     */
    activePlacementResultId() {
      const activePlacement = this.activeBlock?.placements?.[0]
      const fallbackPlacement = this.activeBlock?.placements?.[1]

      if (this.nostoRecommendations[activePlacement]) {
        return this.nostoRecommendations[activePlacement].resultId
      }

      if (this.nostoRecommendations[fallbackPlacement]) {
        return this.nostoRecommendations[fallbackPlacement].resultId
      }

      return false
    },

    /**
     * Compute class based on specific content populated in section settings.
     * @returns {String}
     */
    headerClass() {
      let headerClass = ''

      const hasTitle = Boolean(this.section.title)
      const hasMultipleTabs = this.blocks.length > 1
      const hasActiveCta = Boolean(this.activeCta?.link && this.activeCta?.text)

      if (
        hasTitle &&
        !hasMultipleTabs &&
        !hasActiveCta
      ) {
        headerClass = 'product-recommendations__header--title-only'
      }

      return headerClass
    },
  },

  watch: {

    /**
     * Watch active block to update placements ID.
     * - Skip updating placements if they've already been loaded.
     */
    async activeBlock() {
      const products = await this.getActiveBlockProducts()

      if (products) {
        this.products = products
        this.state = 'ready'
        return
      }

      this.state = 'loading'
      this.setPlacements(this.activeBlock.placements)
    },

    /**
     * Watch Nosto recommendations to see if placement ID has updated.
     */
    async nostoRecommendations() {
      const products = await this.getActiveBlockProducts()

      if (!products) {
        this.state = 'error'
        return
      }

      this.products = products
      this.state = 'ready'
    },

    /**
     * Watch products update carousel.
     */
    products() {
      if (!this.products.length) {
        return
      }

      /**
       * Update slide pagination data.
       */
      this.slides = {
        current: 0,
        last: this.products.length - 1,
      }

      /**
       * Update visible slides data.
       */
      this.visible = [...Array(this.products.length)].map((_, index) => {
        return index < 4
      })

      /**
       * Get min height of carousel.
       */
      this.getMinHeight()
    },

    /**
     * Watch screen width to update min-height.
     */
    screenWidth() {
      this.getMinHeight()
    },
  },

  mounted() {
    this.sectionTitle = this.section.title
    this.activeBlock = this.blocks[0]
    this.getMinHeight()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPlacements: 'nosto/setPlacements',
    }),

    /**
     * Check if currency of nosto product is equal with the store's currency.
     * @returns {Boolean}
     */
    checkCurrencyMatch(product) {
      return product?.price?.currencyCode === cnvs.store.currency
    },

    /**
     * Get products from the nosto recommendations.
     * @param {Boolean|Object} recommendations - Fallback recommendations.
     * @returns {Boolean|Array}
     */
    getRecomendationsProducts(recommendations) {
      if (!recommendations) {
        return false
      }

      if (!recommendations.products.filter(Boolean).length) {
        return false
      }

      return recommendations.products
    },

    /**
     * Get active block placement products if they exist.
     * @returns {Boolean|Array}
     */
    getActiveBlockProducts() {
      return new Promise((resolve) => {
        if (!this.activeBlock.placements.length) {
          resolve(false)
          return
        }

        const activePlacement = this.activeBlock.placements[0]
        const fallbackPlacement = this.activeBlock.placements[1]
        const placementRecommendations = this.nostoRecommendations[activePlacement]
        const fallbackRecommendations = fallbackPlacement
          ? this.nostoRecommendations[fallbackPlacement]
          : false
        const placementProducts = this.getRecomendationsProducts(placementRecommendations)
        const fallbackProducts = this.getRecomendationsProducts(fallbackRecommendations)

        if (placementProducts) {
          resolve(placementProducts)
          return
        }

        if (fallbackProducts) {
          if (this.section.fallbackTitle) {
            this.sectionTitle = this.section.fallbackTitle
          }

          resolve(fallbackProducts)
          return
        }

        resolve(false)
      })
    },

    /**
     * Get min-height of carousel to prevent layout shift when changing tab.
     * - Set arbitrary timeout to give everything time to update.
     */
    getMinHeight() {
      if (this.blocks.length < 2) {
        return
      }

      setTimeout(() => {
        this.minHeight = `${this.$refs.splide.root.offsetHeight}px`
      }, 0)
    },

    /**
     * Handle tab click.
     * @param {Object} block - Clicked block.
     */
    handleTabClick(block) {
      if (this.activeBlock === block) {
        return
      }

      this.activeBlock = block
    },

    /**
     * Handle arrow click.
     * - Using custom function because if we use 'rewind' then it skips any
     *   incomplete pages, if we use 'loop' then it fills empty spaces with
     *   improperly initialised product card components.
     * @param {String} type - Type of arrow, `next` or `previous`.
     */
    handleArrowClick(type) {
      if (type === 'next') {
        if ((this.slides.current + 4) >= this.slides.last) {
          this.$refs.splide.go(0)
          return
        }

        this.$refs.splide.go('>')
        return
      }

      if (this.slides.current === 0) {
        this.$refs.splide.go(this.slides.last)
        return
      }

      this.$refs.splide.go('<')
    },

    /**
     * Handle carousel move event.
     * @param {Number} newIndex - The new visible index.
     */
    handleMove(_, newIndex) {
      this.slides.current = newIndex
    },

    /**
     * Handle visible.
     */
    handleVisibleUpdate(slide, visible) {
      this.visible[slide.index] = visible
    },

    /**
     * Set clicked product id and nosto id to session storage to log them.
     * @param {Object} product - Product object clicked.
     * @param {String} url - URL of product.
     */
    handleViewProduct(product, url) {
      if (!this.activePlacementResultId) {
        window.location = url
        return
      }

      waitFor('nostojs', () => {
        nostojs(api => {
          api.defaultSession()
            .recordAttribution(
              'vp',
              product.id.toString(),
              this.activePlacementResultId,
            )
            .done()
            .then(window.location = url)
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-recommendations';
</style>
